import {ZadCustomer, ZadDeliveryNote, ZadDeliveryNoteWritePayload, ZadSale} from '../../../models/zad'
import {resolveZadUserMessage, ZadApiError} from './errors'
import {getZadSaleCustomerId} from './saleHelpers'

export const ZAD_DELIVERY_NOTE_STATUS_I18N = {
  ACTIVE: 'ZAD.DELIVERY_NOTES.STATUS_ACTIVE',
  CANCELLED: 'ZAD.DELIVERY_NOTES.STATUS_CANCELLED',
} as const

export function todayZadIsoDate(): string {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`
}

export function getZadDeliveryNoteSaleId(
  sale: ZadDeliveryNote['sale'] | string | null | undefined
): string {
  if (!sale) return ''
  if (typeof sale === 'string') return sale
  return sale._id || ''
}

function customerType(sale?: Pick<ZadSale, 'customer' | 'customerSnapshot'> | null): string {
  const snapshotType = String(sale?.customerSnapshot?.type || '').trim().toUpperCase()
  if (snapshotType) return snapshotType
  const customer = sale?.customer
  if (customer && typeof customer !== 'string') {
    return String(customer.type || '').trim().toUpperCase()
  }
  return ''
}

export function canLookupZadDeliveryNote(
  sale?: Pick<ZadSale, 'status' | 'customer'> | null
): boolean {
  return sale?.status === 'VALIDATED' && Boolean(getZadSaleCustomerId(sale.customer))
}

export function canGenerateZadDeliveryNote(
  sale?: Pick<ZadSale, 'status' | 'customer' | 'customerSnapshot'> | null
): boolean {
  if (!canLookupZadDeliveryNote(sale)) return false
  const type = customerType(sale)
  if (type && type !== 'ORGANIZATION') return false
  return true
}

export function isActiveZadDeliveryNote(note?: ZadDeliveryNote | null): boolean {
  return note?.status === 'ACTIVE'
}

export function sanitizeZadDeliveryNoteWritePayload(
  payload: ZadDeliveryNoteWritePayload
): ZadDeliveryNoteWritePayload {
  const body: ZadDeliveryNoteWritePayload = {
    sale: String(payload.sale || '').trim(),
    deliveryDate: String(payload.deliveryDate || '').trim(),
  }
  const notes = String(payload.notes || '').trim()
  if (notes) body.notes = notes
  return body
}

export function resolveZadDeliveryNoteMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    DELIVERY_NOTE_NOT_FOUND: 'ZAD.DELIVERY_NOTES.NOT_FOUND',
    DELIVERY_NOTE_ALREADY_EXISTS: 'ZAD.DELIVERY_NOTES.ALREADY_EXISTS',
    SALE_NOT_FOUND: 'ZAD.SALES.NOT_FOUND',
    SALE_NOT_VALIDATED: 'ZAD.DELIVERY_NOTES.SALE_NOT_VALIDATED',
    SALE_HAS_NO_CUSTOMER: 'ZAD.DELIVERY_NOTES.SALE_HAS_NO_CUSTOMER',
    CUSTOMER_NOT_FOUND: 'ZAD.CUSTOMERS.NOT_FOUND',
    CUSTOMER_NOT_ORGANIZATION: 'ZAD.DELIVERY_NOTES.CUSTOMER_NOT_ORGANIZATION',
    CUSTOMER_TAX_ID_REQUIRED: 'ZAD.DELIVERY_NOTES.CUSTOMER_TAX_ID_REQUIRED',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}

export function liveCustomerTaxId(customer?: ZadSale['customer']): string {
  if (!customer || typeof customer === 'string') return ''
  return String((customer as ZadCustomer).taxId || '').trim()
}
